import { ClassicActionError, classicActionRequest } from './classic-admin-api';

export type ClassicPurchaseStatus = 'verified_pending' | 'granted' | 'revoked';
export interface ClassicPurchase {
  id: string; platform: string; transactionId: string | null; playerId: string; displayName: string | null;
  productId: string; purchasedAt: number; validatedAt: number | null; grantedAt: number | null;
  status: ClassicPurchaseStatus; environment: string; quantity: number; currency: string | null;
  amountMicros: number | null; amountSource: string | null; usdMicros: number | null;
  refundStatus: 'unknown'; verificationSource: string | null;
}
export interface ClassicPurchaseReadiness {
  ledger?: boolean; financialAmounts?: boolean; usdNormalization?: boolean; refundReconciliation?: boolean;
  country?: boolean; reports?: boolean; purchasesEnabled?: boolean; deliveryProtocolReady?: boolean;
  reportsEnabled?: boolean; reportingConfigured?: boolean; refundChecksReady?: boolean;
}
export interface ClassicPurchaseAnalytics {
  since: number; until: number; timeZone: string;
  totals: { purchases: number; buyers: number; units: number; unknownAmounts: number; refundedPurchases: null; verifiedPending: number; granted: number; revoked: number };
  products: { platform: string; productId: string; purchases: number; units: number; buyers: number; usdMicros: number | null }[];
  daily: { day: string; purchases: number; usdMicros: number | null; refunds: null }[];
  hourly?: { hour: number; purchases: number; usdMicros: number | null; refunds: null }[];
  recent: ClassicPurchase[];
  buyers: { playerId: string; displayName: string | null; purchases: number; usdMicros: number | null }[];
  previous: { purchases: number; usdMicros: null }; nextCursor: string | null; limit: number;
  readiness: ClassicPurchaseReadiness;
}
export interface ClassicPurchaseProduct {
  platform: string; productId: string; name: string; iconId: string | null;
  rewards: { id: string; name?: string; amount: number; itemCategory?: number | string }[]; entitlements: string[];
}
export type ClassicPurchaseFilters = { days: number; platform: string; status: string; q: string; cursor: string };
export function getClassicPurchaseAnalytics(filters: ClassicPurchaseFilters, signal?: AbortSignal) {
  const query = new URLSearchParams({ days: String(filters.days) });
  for (const key of ['platform', 'status', 'q', 'cursor'] as const) if (filters[key] && filters[key] !== 'all') query.set(key, filters[key]);
  return classicActionRequest<ClassicPurchaseAnalytics>(`/purchases?${query}`, signal);
}
export const getClassicPurchaseCatalog = (signal?: AbortSignal) => classicActionRequest<{ products: ClassicPurchaseProduct[] }>('/purchases/catalog', signal);
export const getClassicPurchaseDetail = (id: string, signal?: AbortSignal) => classicActionRequest<{ purchase: ClassicPurchase; readiness: ClassicPurchaseReadiness }>(`/purchases/${encodeURIComponent(id)}`, signal);

/** Protected purchase views must discard cached data after a definitive access denial. */
export function isClassicPurchaseAuthorizationFailure(error: unknown): boolean {
  return error instanceof ClassicActionError && (error.status === 401 || error.status === 403);
}

export const getClassicPlayerPurchases = (id: string, cursor = '', signal?: AbortSignal) =>
  classicActionRequest<{ total: number; purchases: ClassicPurchase[]; nextCursor: string | null }>(`/players/${encodeURIComponent(id)}/purchases${cursor ? `?${new URLSearchParams({ cursor })}` : ''}`, signal);
